import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, Clock, User } from 'lucide-react';
import {
  bylineFor,
  formatInsightDate,
  getFeaturedArticle,
  readingTimeFor,
  topicFor,
} from '@/data/insights';
import { Reveal } from '../Reveal';
import { useLiveTick } from '@/lib/liveContent';

// One prominent featured article beneath the hero. Selection is centrally
// configurable via FEATURED_ARTICLE_ID in src/data/insights.js.
export const FeaturedInsight = () => {
  useLiveTick();
  const featured = getFeaturedArticle();
  if (!featured) return null;

  const topic = topicFor(featured.primaryTopic);
  const reading = readingTimeFor(featured);

  return (
    <section aria-labelledby="featured-insight-heading" data-testid="featured-insight" className="bg-mist py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <article className="grid overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card lg:grid-cols-2">
            <Link
              to={`/insights/${featured.slug}`}
              tabIndex={-1}
              aria-hidden="true"
              className="block overflow-hidden"
            >
              <img
                src={featured.image?.src}
                alt={featured.image?.alt || featured.title}
                width={featured.image.width}
                height={featured.image.height}
                data-testid="featured-insight-image"
                className="aspect-[16/10] h-full w-full object-cover lg:aspect-auto"
              />
            </Link>
            <div className="flex flex-col justify-center p-8 sm:p-10 lg:p-12">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-navy-900 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-gold-500">
                  Featured Insight
                </span>
                {topic && (
                  <span
                    className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
                    style={{ backgroundColor: topic.soft, color: topic.dark }}
                  >
                    {topic.label}
                  </span>
                )}
              </div>
              <h2 id="featured-insight-heading" data-testid="featured-insight-title" className="mt-5 text-balance text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
                {featured.title}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{featured.excerpt}</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" aria-hidden="true" />
                  {bylineFor(featured)}
                </span>
                <time dateTime={featured.publishedAt} className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                  {formatInsightDate(featured.publishedAt)}
                </time>
                {reading && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                    {reading} min read
                  </span>
                )}
              </div>
              <div className="mt-8">
                <Link
                  to={`/insights/${featured.slug}`}
                  data-testid="featured-insight-read-btn"
                  className="group inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-700"
                >
                  Read Featured Insight
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </article>
        </Reveal>
      </div>
    </section>
  );
};
